import { ReactElement } from "react";
import { getHashParams } from "@common/utils/router";
import { useRouter } from "next/router";
import { Container, Flex, Loader, useMantineTheme } from "@mantine/core";

import { purchasesStore } from "@stores/purchases";
import { canTryProPlan, isCanceledOrDeleted } from "@stores/purchases/utils";
import { paymentMethodsStore } from "@stores/payment-methods";
import NewPurchaseCard from "@components/account/purchases/NewPurchaseCard/NewPurchaseCard";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { useAccount } from "@stores/session/hooks";
import PurchasesListing from "@components/account/purchases/PurchasesListing";
import NewInstanceDropdown from "@components/account/new-instance/NewInstanceDropdown";
import PageTitle from "@common/components/thirdParty/mantine/text/PageTitle";

export default function InstancesListingPage(): ReactElement {
  const theme = useMantineTheme();

  const router = useRouter();

  const account = useAccount();

  const {
    isLoading: loadingPurchases,
    error: purchasesError,
    data: purchases,
  } = purchasesStore.useGetPurchases();

  // payment
  const { data: defaultPaymentMethod } =
    paymentMethodsStore.useDefaultPaymentMethod();

  // instances
  const runningPurchases = purchases?.filter(
    (purchase) => !isCanceledOrDeleted(purchase),
  );
  const canceledPurchases = purchases?.filter((purchase) =>
    isCanceledOrDeleted(purchase),
  );

  // pro trial
  function getSectionForProTrial(): string | undefined {
    const hash = getHashParams(router.asPath);
    if (purchases && hash.section === "pro-trial") {
      const firstPurchaseThatCanTryProPlan = purchases.find((purchase) =>
        canTryProPlan(purchase),
      );
      if (firstPurchaseThatCanTryProPlan) {
        return `pro-trial-${firstPurchaseThatCanTryProPlan.id}`;
      }
    }

    return undefined;
  }
  const defaultSection = getSectionForProTrial();

  return (
    <Container>
      {loadingPurchases ? (
        <Loader />
      ) : (
        <>
          {!purchases || purchases.length === 0 ? (
            <NewPurchaseCard account={account} purchases={purchases} />
          ) : (
            <>
              {runningPurchases && runningPurchases.length > 0 && (
                <Flex
                  align="center"
                  direction="row"
                  justify="space-between"
                  maw={theme.breakpoints.sm}
                  mb="xl"
                  w="100%"
                >
                  <PageTitle m={0}>Instances</PageTitle>

                  <NewInstanceDropdown />
                </Flex>
              )}

              {/* Running purchases */}
              {account && runningPurchases && runningPurchases.length > 0 ? (
                <PurchasesListing
                  purchases={runningPurchases}
                  account={account}
                  paymentMethod={defaultPaymentMethod}
                  defaultSection={defaultSection}
                />
              ) : (
                <NewPurchaseCard account={account} purchases={purchases} />
              )}

              {/* Canceled purchases */}
              {account && canceledPurchases && canceledPurchases.length > 0 && (
                <>
                  <PageTitle m={0} mb="md" mt="xl">
                    Canceled Instances
                  </PageTitle>
                  <PurchasesListing
                    purchases={canceledPurchases}
                    account={account}
                    paymentMethod={defaultPaymentMethod}
                    defaultSection={defaultSection}
                  />
                </>
              )}

              {/* Error */}
              <ErrorMessage error={purchasesError} />
            </>
          )}
        </>
      )}
    </Container>
  );
}
